import { useEffect, useState } from "react";
import MiniNavigationTabs from "./miniNavigationTabs";
import MainContentLayout from "../../components/mainContentLayout";
import PageHeader from "../../components/pageHeader";
import LoadingOverlay from "../../components/loadingOverlay";
import { API_BASE } from "../../config/apiConfig";
import FilePreviewModal from "../../components/filePreviewModal";
import { useNavigate } from "react-router-dom";

export default function PlayerAttendance() {
  const [items, setItems] = useState([]);
  const [month, setMonth] = useState(new Date().getMonth());
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const token = sessionStorage.getItem("access_token");
  const navigate = useNavigate();

  async function loadData() {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/player/attendance?year=${year}&month=${month}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.success) setItems(data.items);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, [month, year]);

  return (
    <MainContentLayout>
      <PageHeader title="My Attendance" backTo="/dashboard" />
      <MiniNavigationTabs />

      {/* Filters + Apply Leave */}
      <div className="mb-4 flex justify-between items-center">
        <div>
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="border rounded p-2 mr-2"
          >
            {Array.from({ length: 12 }).map((_, i) => (
              <option key={i} value={i}>
                {new Date(0, i).toLocaleString("default", { month: "long" })}
              </option>
            ))}
          </select>

          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="border rounded p-2"
          >
            {Array.from({ length: 6 }).map((_, i) => {
              const y = year - 2 + i;
              return (
                <option key={y} value={y}>
                  {y}
                </option>
              );
            })}
          </select>
        </div>

        <button
          onClick={() => navigate("/player-apply-leave")}
          className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700"
        >
          Apply Leave
        </button>
      </div>

      {/* Attendance List */}
      <ul className="space-y-3">
        {items.map((it, idx) => {
          const dt = new Date(it.eventDate);
          const leave = it.leave;

          let statusNode = null;

          // ===============================================================
          //  LEAVE DISPLAY
          // ===============================================================
          if (leave) {
            if (leave.type === "Medical Leave") {
              statusNode = (
                <div className="text-blue-600 flex flex-col items-end gap-1">
                  <span className="font-semibold">MC</span>
                  {leave.documentUrl && (
                    <button
                      onClick={() => setPreviewUrl(leave.documentUrl)}
                      className="text-xs underline text-blue-600"
                    >
                      View Document
                    </button>
                  )}
                </div>
              );
            } else if (
              leave.type === "Tournament Leave" ||
              leave.type === "Event Leave"
            ) {
              statusNode = (
                <div className="flex flex-col items-end">
                  <span
                    className={
                      leave.type === "Tournament Leave"
                        ? "text-purple-600 font-semibold"
                        : "text-green-600 font-semibold"
                    }
                  >
                    {leave.type.replace(" Leave", "")}
                  </span>
                  {leave.activityName && (
                    <span className="text-sm text-gray-500">
                      {leave.activityName}
                    </span>
                  )}
                </div>
              );
            } else if (leave.type === "Emergency Leave") {
              statusNode = (
                <div className="flex flex-col items-end">
                  <span className="text-pink-600 font-semibold">
                    Emergency Leave
                  </span>
                  {leave.emergencyReason && (
                    <span className="text-sm text-gray-500">
                      Reason: {leave.emergencyReason}
                    </span>
                  )}
                  {leave.remark && (
                    <span className="text-sm text-gray-400 italic">
                      Remark: {leave.remark}
                    </span>
                  )}
                </div>
              );
            }
          }

          // ===============================================================
          //  ATTENDED DISPLAY
          // ===============================================================
          else if (it.status !== "Absent") {
            const isLate = it.status.startsWith("Late");
            statusNode = (
              <div
                className={`${isLate ? "text-orange-600" : "text-green-600"} flex flex-col items-end`}
              >
                <span>{it.status}</span>
                {it.checkInTime && (
                  <span className="text-sm text-gray-500">
                    Checked in: {it.checkInTime}
                  </span>
                )}
              </div>
            );
          }

          // ===============================================================
          //  ABSENT DISPLAY
          // ===============================================================
          else {
            statusNode = <span className="text-red-600 font-semibold">Absent</span>;
          }

          return (
            <li
              key={it.eventId}
              className="border rounded p-3 flex justify-between items-center"
            >
              <div>
                <div>
                  {idx + 1}.{" "}
                  <span className="font-semibold">{dt.toLocaleString()}</span>
                </div>
                <div className="text-gray-500 text-sm">{it.event.name}</div>
              </div>

              <div className="text-right">{statusNode}</div>
            </li>
          );
        })}
      </ul>

      <FilePreviewModal
        url={previewUrl}
        onClose={() => setPreviewUrl(null)}
      />

      <LoadingOverlay show={loading} />
    </MainContentLayout>
  );
}
